import { useAuth } from '../providers/auth-provider';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Separator,
} from '@t90/ui';
import { formatDate, formatPhone } from '@t90/common';

export function ProfilePage() {
  const { trainee } = useAuth();

  const initials = trainee
    ? (trainee.full_name ?? trainee.email ?? '')
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    : 'T';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Profile</h1>
        <p className="text-muted-foreground">
          Your personal and program information
        </p>
      </div>

      {/* Profile Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={trainee?.avatar_url ?? undefined} />
              <AvatarFallback className="text-2xl bg-t90-navy text-t90-gold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="text-center sm:text-left">
              <h2 className="text-xl font-bold">
                {trainee?.full_name ?? 'Unknown'}
              </h2>
              <p className="text-muted-foreground">{trainee?.email}</p>
              <Badge className="mt-2" variant={getStatusVariant(trainee?.status ?? 'Pending')}>
                {trainee?.status}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal Information */}
      <Card>
        <CardHeader>
          <CardTitle>Personal Information</CardTitle>
          <CardDescription>Your contact details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow label="Full Name" value={trainee?.full_name ?? '-'} />
          <Separator />
          <InfoRow label="Email" value={trainee?.email ?? '-'} />
          <Separator />
          <InfoRow label="Phone" value={trainee?.phone ? formatPhone(trainee.phone) : '-'} />
        </CardContent>
      </Card>

      {/* Program Information */}
      <Card>
        <CardHeader>
          <CardTitle>Program Information</CardTitle>
          <CardDescription>Your enrollment details</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <InfoRow label="Status" value={trainee?.status ?? '-'} />
          <Separator />
          <InfoRow label="Location" value={trainee?.location_id ?? '-'} />
          <Separator />
          <InfoRow label="Cohort" value={trainee?.cohort ?? '-'} />
          <Separator />
          <InfoRow
            label="Start Date"
            value={trainee?.start_date ? formatDate(trainee.start_date) : '-'}
          />
          <Separator />
          <InfoRow
            label="Expected End Date"
            value={trainee?.graduation_target_date ? formatDate(trainee.graduation_target_date) : '-'}
          />
        </CardContent>
      </Card>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function getStatusVariant(status: string): 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Graduated':
      return 'default';
    case 'On Leave':
      return 'warning';
    case 'Terminated':
      return 'destructive';
    case 'Pending':
      return 'secondary';
    default:
      return 'secondary';
  }
}
